import { AxiosRequestConfig } from 'axios'
import { useApi } from '../hooks'
import {
  DeviceMessage,
  Valve,
  ValveSensor,
  ValveSensorAggregate,
  ValveSensorAggregateQuery,
  ValveSensorQuery,
  ValveUpdateInput
} from '../types'
import ValveStatus from '../types/ValveStatus'
import { AlertGroup, AlertTemplate } from '../types/Alerts'

const api = useApi('v2')

/**
 * Get valves.
 */
export async function getValves(config?: Omit<AxiosRequestConfig, 'params'>): Promise<Valve[]> {
  const response = await api.get('/valves', config)

  return response.data
}

/**
 * Get a Valve by name.
 */
export async function getValve(name: string, config?: AxiosRequestConfig): Promise<Valve> {
  const response = await api.get(`/valves/${name}`, config)

  return response.data
}

/**
 * Update a valve.
 */
export async function updateValve(
  name: string,
  input: ValveUpdateInput,
  config?: AxiosRequestConfig
): Promise<Valve> {
  const response = await api.patch(`/valves/${name}`, input, config)

  return response.data
}

/**
 * Get the valve sensor.
 */
export async function getValveSensor(
  name: string,
  config?: AxiosRequestConfig
): Promise<ValveSensor> {
  const response = await api.get(`/valves/${name}/sensor`, config)

  return response.data
}

/**
 * Get the valve sensor.
 */
export async function getValveSensors(
  name: string,
  query: ValveSensorQuery,
  config?: Omit<AxiosRequestConfig, 'params'>
): Promise<ValveSensor[]> {
  const response = await api.get(`/valves/${name}/sensors`, {
    ...config,
    params: query
  })

  return response.data
}

/**
 * Get the valve sensor aggregates.
 */
export async function getValveSensorAggregates(
  name: string,
  query: ValveSensorAggregateQuery,
  config?: Omit<AxiosRequestConfig, 'params'>
): Promise<ValveSensorAggregate[]> {
  const response = await api.get(`/valves/${name}/sensor-aggregates`, {
    ...config,
    params: query
  })

  return response.data
}

/**
 * Get the valve status.
 */
export async function getValveStatus(
  name: string,
  config?: AxiosRequestConfig
): Promise<ValveStatus> {
  const response = await api.get(`/valves/${name}/status`, config)

  return response.data
}

type ValveActionInput = {
  ruuvi_pair?: [
    {
      in: string
      out: string
    }
  ]
  temp_target?: number
  min_position?: number
  update_cycle?: number
  get_status?: 1
  write?: string
  setpoint?: number
}

/**
 * Invoke a valve action.
 */
export async function invokeValveAction(
  name: string,
  input: ValveActionInput,
  config?: AxiosRequestConfig
): Promise<void> {
  await api.post(`/valves/${name}/action`, input, config)
}

/**
 * Get the valve messages.
 */
export async function getValveMessages(
  name: string,
  config?: AxiosRequestConfig
): Promise<DeviceMessage[]> {
  const response = await api.get(`/valves/${name}/messages`, config)

  return response.data
}

export async function getValveAlertTemplates(
  config?: AxiosRequestConfig
): Promise<AlertTemplate[]> {
  const response = await api.get('/valves/alert-templates', config)

  return response.data
}

export async function getValveAlertRules(
  name: string,
  config?: AxiosRequestConfig
): Promise<AlertGroup[]> {
  const response = await api.get(`/valves/${name}/alert-rules`, config)

  return response.data
}

export async function updateValveAlertRules(
  name: string,
  input: AlertGroup[],
  config?: AxiosRequestConfig
): Promise<AlertGroup[]> {
  const response = await api.put(`/valves/${name}/alert-rules`, input, config)

  return response.data
}

export async function pollValveStatus(
  name: string,
  { timeout, ...config }: AxiosRequestConfig = {}
): Promise<ValveStatus> {
  await invokeValveAction(
    name,
    {
      get_status: 1
    },
    { ...config }
  )

  const ONE_SECOND = 1000
  const THIRTY_SECONDS = 30 * ONE_SECOND
  timeout = timeout ?? THIRTY_SECONDS

  const start = Date.now()

  while (true) {
    try {
      return await getValveStatus(name, { timeout, ...config })
    } catch {
      if (Date.now() - start > timeout) {
        throw new Error(`Failed to get status within 30 seconds`)
      }

      // Wait for one second before trying again.
      await sleep(ONE_SECOND)
    }
  }
}

export async function pollValveSetTemperature(
  name: string,
  timeout = 30_000,
  config?: AxiosRequestConfig
): Promise<number> {
  await invokeValveAction(
    name,
    {
      get_status: 1
    },
    config
  )

  const ONE_SECOND = 1000
  const THREE_SECONDS = 3 * ONE_SECOND
  const start = Date.now()

  // Wait for three seconds for the action to to be processed.
  await sleep(THREE_SECONDS)

  while (true) {
    try {
      const status = await getValveStatus(name, config)

      if (status.set_temp === null) {
        // Break the loop if the timeout has been reached.
        if (Date.now() - start > timeout) {
          break
        }

        // Wait for one second before trying again.
        await sleep(ONE_SECOND)

        continue
      }

      return typeof status.set_temp === 'string' ? parseFloat(status.set_temp) : status.set_temp
    } catch {
      // Break the loop if the timeout has been reached.
      if (Date.now() - start > timeout) {
        break
      }

      // Wait for one second before trying again.
      await sleep(ONE_SECOND)
    }
  }

  throw new Error(`Failed to get set temperature within 30 seconds`)
}

/**
 * Update the set temperature of the valve.
 */
export async function updateValveSetTemperature(
  name: string,
  setTemperature: number,
  { timeout, ...config }: AxiosRequestConfig = {}
): Promise<ValveStatus> {
  // Invoke action to set a new set point.
  await invokeValveAction(name, {
    temp_target: setTemperature
  })

  const ONE_SECOND = 1000
  const THIRTY_SECONDS = 30 * ONE_SECOND

  const start = Date.now()
  timeout = timeout ?? THIRTY_SECONDS

  while (true) {
    try {
      // Get the status of the valve.
      const status = await getValveStatus(name, config)

      // Check if the temperature does not match the new temperature.
      if (status.set_temp !== setTemperature) {
        // Break the loop if the timeout has been reached.
        if (Date.now() - start > timeout) {
          break
        }

        // Wait for one second before trying again.
        await sleep(ONE_SECOND)
        continue
      }

      return status
    } catch {
      // Break the loop if the timeout has been reached.
      if (Date.now() - start > timeout) {
        break
      }

      // Wait for one second before trying again.
      await sleep(ONE_SECOND)
    }
  }

  throw new Error('Failed to verify set temperature within 30 seconds')
}

/**
 * Update the set point of the valve.
 */
export async function updateValveSetPoint(
  name: string,
  setPoint: number,
  { timeout, ...config }: AxiosRequestConfig = {}
): Promise<ValveStatus> {
  // Invoke action to set a new set point.
  await invokeValveAction(
    name,
    {
      setpoint: setPoint
    },
    config
  )

  const ONE_SECOND = 1000
  const THREE_SECONDS = 3 * ONE_SECOND
  const THIRTY_SECONDS = 30 * ONE_SECOND

  // Wait for three seconds for the action to to be processed.
  await sleep(THREE_SECONDS)

  // Invoke action to get the status so that we can verify the new set point.
  await invokeValveAction(name, { get_status: 1 }, config)

  const start = Date.now()
  timeout = timeout ?? THIRTY_SECONDS

  while (true) {
    try {
      // Get the status of the valve.
      const status = await getValveStatus(name, { ...config })

      // Check if the set point matches the new set point.
      if (status.set_temp !== null || status.sp !== setPoint) {
        // Break the loop if the timeout has been reached.
        if (Date.now() - start > timeout) {
          break
        }

        // Wait for one second before trying again.
        await sleep(ONE_SECOND)
        continue
      }

      return status
    } catch {
      // Break the loop if the timeout has been reached.
      if (Date.now() - start > timeout) {
        break
      }

      // Wait for one second before trying again.
      await sleep(ONE_SECOND)
    }
  }

  throw new Error('Failed to verify set point within 30 seconds')
}

/**
 * Sleep for the given delay.
 */
function sleep(delay: number) {
  return new Promise((resolve) => setTimeout(resolve, delay))
}
